/**
 * TransitSkillEffect — Specter phantom-path apotheosis: positional echo.
 *
 * Teleport to any location within 8 tiles that you've occupied in the last
 * 30 seconds. A Doppelganger-quality decoy persists at the origin for 5s.
 *
 * Position history is maintained as a ring buffer (30 entries, 1 per second).
 * The ring buffer accumulates via update() even while on cooldown — the player's
 * traversal of the last 30 seconds is always available for Transit to use.
 *
 * On activation: deps.targeting.getTargetPosition() returns the selected
 * history point (or null if none in range — fizzle equivalent). Teleport to
 * that position. Spawn a Doppelganger-quality decoy at origin via summons.
 *
 * Instant activation. Very long cooldown. Fizzle at low XP. Costs 2 slots.
 *
 * Implements SkillEffect directly. Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see specter-spec.md § Transit
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface TransitDeps {
  readonly targeting: {
    shouldFizzle(): boolean;
    /**
     * Current player position in pixels (for decoy spawn and history recording).
     */
    getCurrentPosition(): { x: number; y: number };
    /**
     * Returns the target position selected from history (within maxRangeTiles),
     * or null if no valid position is available.
     */
    getTargetPosition(): { x: number; y: number } | null;
  };
  readonly movement: {
    /** Instantly move the player to the given world position. */
    teleportTo(x: number, y: number): void;
  };
  readonly summons: {
    /** Spawn a Doppelganger-quality decoy at the given world position. */
    spawn(
      archetype: string,
      opts: { durationMs: number; aggroWeight: number },
      x: number,
      y: number,
    ): string;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface TransitConfig {
  readonly cooldownMs: number;
  readonly decoyArchetype: string;
  readonly decoyDurationMs: number;
  readonly historyCapacity: number;
  readonly historyIntervalMs: number;
  readonly maxRangeTiles: number;
  readonly tileSizePx: number;
}

const DEFAULT_CONFIG: TransitConfig = {
  cooldownMs: 5000,            // very long cooldown tier
  decoyArchetype: 'decoy',     // Doppelganger-quality decoy
  decoyDurationMs: 5000,       // 5-second origin decoy
  historyCapacity: 30,         // 30-second ring buffer
  historyIntervalMs: 1000,     // record one position per second
  maxRangeTiles: 8,            // 8-tile teleport range
  tileSizePx: 32,
};

// ---------------------------------------------------------------------------
// Position history entry
// ---------------------------------------------------------------------------

export interface PositionEntry {
  x: number;
  y: number;
  timestampMs: number;
}

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class TransitSkillEffect implements SkillEffect {
  readonly skillId = 'transit';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly failLabel = 'FIZZLE';

  private readonly deps: TransitDeps;
  private readonly config: TransitConfig;

  private _cooldownRemaining = 0;
  private _fizzled = false;
  private _activationCount = 0;
  private _decoyId = '';
  private _lastOrigin: { x: number; y: number } = { x: 0, y: 0 };
  private _lastTarget: { x: number; y: number } = { x: 0, y: 0 };

  // Position history ring buffer
  private _history: PositionEntry[] = [];
  private _historyAccumulatorMs = 0;
  private _simTimestampMs = 0;

  constructor(deps: TransitDeps, config: Partial<TransitConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get fizzled(): boolean { return this._fizzled; }
  get activationCount(): number { return this._activationCount; }
  get decoyId(): string { return this._decoyId; }
  get lastOrigin(): Readonly<{ x: number; y: number }> { return this._lastOrigin; }
  get lastTarget(): Readonly<{ x: number; y: number }> { return this._lastTarget; }
  get positionHistory(): readonly PositionEntry[] { return this._history; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    this._fizzled = this.deps.targeting.shouldFizzle();
    if (this._fizzled) {
      this._cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    const target = this.deps.targeting.getTargetPosition();
    if (!target) {
      // No valid history position in range — fail silently (not a fizzle)
      return false;
    }

    const origin = this.deps.targeting.getCurrentPosition();
    this._lastOrigin = { ...origin };
    this._lastTarget = { ...target };

    // Spawn Doppelganger-quality decoy at origin
    this._decoyId = this.deps.summons.spawn(
      this.config.decoyArchetype,
      { durationMs: this.config.decoyDurationMs, aggroWeight: 1.0 },
      origin.x,
      origin.y,
    );
    // TODO: scene-wiring — apply confused status to nearby enemies when transit decoy expires
    // The summons.spawn() interface returns an ID but has no lifecycle/expiry callback.
    // On decoy expiry (after decoyDurationMs), all enemies within the decoy's aggro radius
    // should receive the 'confused' status effect. This requires either:
    //   (a) an onExpiry callback on the summons interface, or
    //   (b) a deferred call scheduled via the scene's timer system.
    // Neither mechanism is available in TransitDeps today. Wire this when the summon
    // system supports expiry hooks or when scene-level timers are injected.

    // Teleport player to target
    this.deps.movement.teleportTo(target.x, target.y);

    this._cooldownRemaining = this.config.cooldownMs;
    this._activationCount++;

    return true;
  }

  deactivate(): void {
    // Instant skill — no toggle deactivation
  }

  isActive(): boolean {
    return false; // Transit is always instant — no persistent active window
  }

  update(delta: number): void {
    // Tick cooldown
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    // Maintain position history ring buffer (every historyIntervalMs)
    this._simTimestampMs += delta;
    this._historyAccumulatorMs += delta;
    if (this._historyAccumulatorMs >= this.config.historyIntervalMs) {
      this._historyAccumulatorMs = 0;
      const pos = this.deps.targeting.getCurrentPosition();
      const entry: PositionEntry = { x: pos.x, y: pos.y, timestampMs: this._simTimestampMs };

      if (this._history.length >= this.config.historyCapacity) {
        this._history.shift(); // remove oldest entry
      }
      this._history.push(entry);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
    this._history = [];
  }
}
